import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import CelebrationIcon from '@mui/icons-material/CelebrationRounded';
import { onboardingApi } from '@/api/onboarding';
import { useAuthStore } from '@/store/authStore';
import { PALETTE } from '@/theme/tokens';

/**
 * First-run checklist.
 *
 * Not a slideshow. Every step's done/not-done comes from the server, computed
 * from real data on each call, so the list ticks itself off as the user
 * actually works — they connect an account, come back, and step 2 is already
 * green. That also means somebody who set everything up before opening this
 * sees a finished checklist instead of being walked through work they've done.
 *
 * It opens itself once per account. After that it only appears when asked for,
 * via "Getting started" in the profile menu.
 */

const STEP_NUM = { 0: '1', 1: '2', 2: '3', 3: '4' };

export default function OnboardingTour({ open: openProp, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Controlled when the profile menu opens it; self-managed on first run.
  const controlled = openProp !== undefined;
  const [autoOpen, setAutoOpen] = useState(false);
  const open = controlled ? openProp : autoOpen;

  const { data } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => onboardingApi.status(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  /*
   * Re-check on every navigation.
   *
   * This is what makes it feel live: the user leaves to connect an account and
   * comes back, and the step is already ticked without a refresh. Cheaper and
   * steadier than polling, because progress only ever changes as a result of
   * the user going somewhere and doing something.
   */
  useEffect(() => {
    if (isAuthenticated) {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    }
  }, [location.pathname, isAuthenticated, queryClient]);

  // Auto-open exactly once, and only for an account that hasn't skipped it and
  // hasn't already finished everything.
  useEffect(() => {
    if (controlled || !data) return;
    if (!data.dismissed && !data.is_complete) setAutoOpen(true);
  }, [controlled, data]);

  const dismiss = useMutation({
    mutationFn: (reason) => onboardingApi.dismiss({ reason }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['onboarding-status'], fresh);
      close();
    },
    // Even if the request fails, get out of the user's way — a checklist that
    // won't close is worse than one that reappears.
    onError: () => close(),
  });

  const close = () => {
    setAutoOpen(false);
    onClose?.();
  };

  if (!data) return null;

  const { steps = [], completed = 0, total = 4, is_complete: isComplete } = data;
  const pct = total ? (completed / total) * 100 : 0;

  const go = (step) => {
    close();
    navigate(step.action_url);
  };

  return (
    <Dialog
      open={!!open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      aria-labelledby="onboarding-title"
      slotProps={{ paper: { sx: { borderRadius: 4, overflow: 'hidden' } } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3.5, pt: 3.5, pb: 3,
          position: 'relative',
          background: 'linear-gradient(135deg,#EEF0FF 0%,#F5F1FF 55%,#FFFFFF 100%)',
        }}
      >
        <IconButton
          onClick={() => dismiss.mutate('skipped')}
          aria-label="Close getting started"
          sx={{ position: 'absolute', top: 12, right: 12, color: 'text.disabled' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography
          id="onboarding-title"
          sx={{ fontWeight: 800, fontSize: 22, letterSpacing: '-.02em', pr: 4 }}
        >
          {isComplete ? "You're all set" : 'Getting started'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isComplete
            ? 'Every step is done. Your content will publish on schedule from here.'
            : 'Four steps between you and your first scheduled post. They tick off automatically as you go.'}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2.5 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              flex: 1, height: 7, borderRadius: 999,
              bgcolor: 'rgba(61,90,254,.14)',
              '& .MuiLinearProgress-bar': { borderRadius: 999 },
            }}
          />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary' }}>
            {completed} of {total}
          </Typography>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 2, py: 1.5 }}>
        <Stack spacing={0.5}>
          {steps.map((step, i) => {
            const isCurrent = !step.done && data.current === step.key;
            return (
              <Stack
                key={step.key}
                direction="row"
                spacing={2}
                sx={{
                  p: 2, borderRadius: 3,
                  alignItems: 'flex-start',
                  bgcolor: isCurrent ? 'rgba(61,90,254,.06)' : 'transparent',
                  border: '1px solid',
                  borderColor: isCurrent ? 'rgba(61,90,254,.22)' : 'transparent',
                  opacity: step.done ? 0.62 : 1,
                  transition: 'all .2s',
                }}
              >
                {/* Marker: a tick once done, otherwise the step number. */}
                <Box
                  sx={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 13,
                    bgcolor: step.done ? '#DCFCE7' : isCurrent ? 'primary.main' : PALETTE.workspace,
                    color: step.done ? '#16A34A' : isCurrent ? '#fff' : 'text.secondary',
                  }}
                >
                  {step.done ? <CheckCircleIcon sx={{ fontSize: 19 }} /> : STEP_NUM[i]}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700, fontSize: 14.5, lineHeight: 1.3,
                      textDecoration: step.done ? 'line-through' : 'none',
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.5 }}>
                    {step.body}
                  </Typography>

                  {!step.done && (
                    <Button
                      size="small"
                      variant={isCurrent ? 'contained' : 'text'}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                      onClick={() => go(step)}
                      sx={{
                        mt: 1.25, borderRadius: 999, textTransform: 'none',
                        fontWeight: 700, fontSize: 13,
                        px: isCurrent ? 2 : 1,
                      }}
                    >
                      {step.action_label}
                    </Button>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3.5, py: 2, borderTop: `1px solid ${PALETTE.cardBorder}` }}
      >
        {isComplete ? (
          <>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CelebrationIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.secondary' }}>
                Nothing left to do.
              </Typography>
            </Stack>
            <Button
              variant="contained"
              onClick={() => dismiss.mutate('completed')}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700, px: 3 }}
            >
              Finish
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => dismiss.mutate('skipped')}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              Skip for now
            </Button>
            <Typography variant="caption" color="text.disabled">
              Reopen any time from your profile menu
            </Typography>
          </>
        )}
      </Stack>
    </Dialog>
  );
}
